import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Flame, Beef, Wheat, Droplet, Zap, Target, Award, Scale } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import MainLayout from "../components/MainLayout";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const DashboardPage = () => {
  const { user, getAuthHeaders, API } = useAuth();
  const [todayData, setTodayData] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [streakData, setStreakData] = useState(null);
  const [predictionData, setPredictionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [todayRes, historyRes, streakRes, predictionRes] = await Promise.all([
        axios.get(`${API}/meals/today`, { headers: getAuthHeaders() }),
        axios.get(`${API}/meals/history?days=7`, { headers: getAuthHeaders() }),
        axios.get(`${API}/stats/streak`, { headers: getAuthHeaders() }),
        axios.get(`${API}/stats/prediction`, { headers: getAuthHeaders() })
      ]);
      
      setTodayData(todayRes.data);
      setHistoryData(historyRes.data.history);
      setStreakData(streakRes.data);
      setPredictionData(predictionRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calorieGoal = todayData?.goals?.calories || user?.calorie_goal || 2000;
  const caloriesConsumed = todayData?.totals?.calories || 0;
  const caloriesRemaining = calorieGoal - caloriesConsumed;
  const progressPercentage = Math.min((caloriesConsumed / calorieGoal) * 100, 100);

  const macroData = [
    { name: "Proteína", value: todayData?.totals?.protein || 0, color: "#4A6741", goal: todayData?.goals?.protein || 150 },
    { name: "Carbos", value: todayData?.totals?.carbs || 0, color: "#E09F3E", goal: todayData?.goals?.carbs || 250 },
    { name: "Grasa", value: todayData?.totals?.fat || 0, color: "#E07A5F", goal: todayData?.goals?.fat || 65 }
  ];

  const chartData = historyData.map(day => ({
    date: new Date(day.date).toLocaleDateString('es-ES', { weekday: 'short' }),
    calories: Math.round(day.totals.calories),
    goal: calorieGoal
  })).reverse();

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-muted-foreground">Cargando datos...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="dashboard-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold">Dashboard</h1>
            <p className="text-muted-foreground">Resumen de tu nutrición</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Hoy</p>
            <p className="font-heading font-semibold">
              {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>

        {/* Streak & Prediction Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Streak Card */}
          {streakData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-accent/10 to-primary/10 border border-accent/20 rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold">Racha Actual</h3>
                  <p className="text-sm text-muted-foreground">Días cumpliendo objetivo</p>
                </div>
              </div>
              
              <div className="flex items-end justify-between">
                <div>
                  <p className="font-mono text-5xl font-bold text-accent" data-testid="current-streak">
                    {streakData.current_streak}
                  </p>
                  <p className="text-sm text-muted-foreground">días consecutivos</p>
                </div>
                <div className="text-right space-y-1">
                  <div className="flex items-center gap-2 justify-end">
                    <Award className="w-4 h-4 text-primary" />
                    <span className="text-sm">Mejor racha: <span className="font-mono font-semibold">{streakData.best_streak}</span></span>
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <Target className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Éxito: <span className="font-mono font-semibold">{streakData.success_rate}%</span></span>
                  </div>
                </div>
              </div>
              
              {streakData.current_streak > 0 && (
                <div className="mt-4 p-3 bg-accent/10 rounded-xl">
                  <p className="text-sm text-accent font-medium">
                    {streakData.current_streak >= 7 
                      ? "🔥 ¡Increíble! Llevas más de una semana cumpliendo." 
                      : streakData.current_streak >= 3 
                        ? "💪 ¡Vas muy bien! Mantén el ritmo."
                        : "✨ ¡Buen comienzo! Sigue así."}
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Prediction Card */}
          {predictionData?.has_prediction && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`border rounded-2xl p-6 ${
                predictionData.direction === "déficit" 
                  ? "bg-gradient-to-br from-primary/10 to-green-500/10 border-primary/20"
                  : predictionData.direction === "superávit"
                    ? "bg-gradient-to-br from-chart-carbs/10 to-accent/10 border-chart-carbs/20"
                    : "bg-card border-border/50"
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  predictionData.direction === "déficit" ? "bg-primary/20" : 
                  predictionData.direction === "superávit" ? "bg-chart-carbs/20" : "bg-muted"
                }`}>
                  <Scale className={`w-6 h-6 ${
                    predictionData.direction === "déficit" ? "text-primary" : 
                    predictionData.direction === "superávit" ? "text-chart-carbs" : "text-muted-foreground"
                  }`} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold">Predicción Corporal</h3>
                  <p className="text-sm text-muted-foreground">Basado en {predictionData.days_tracked} días</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Balance diario:</span>
                  <span className={`font-mono font-semibold ${
                    predictionData.daily_difference > 0 ? "text-chart-carbs" : 
                    predictionData.daily_difference < 0 ? "text-primary" : "text-muted-foreground"
                  }`}>
                    {predictionData.daily_difference > 0 ? "+" : ""}{predictionData.daily_difference} kcal
                  </span>
                </div>
                
                <div className="h-px bg-border/50" />
                
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className={`font-mono text-lg font-bold ${
                      predictionData.weekly_change_kg > 0 ? "text-chart-carbs" : "text-primary"
                    }`}>
                      {predictionData.weekly_change_kg > 0 ? "+" : ""}{predictionData.weekly_change_kg}kg
                    </p>
                    <p className="text-xs text-muted-foreground">1 semana</p>
                  </div>
                  <div>
                    <p className={`font-mono text-lg font-bold ${
                      predictionData.monthly_change_kg > 0 ? "text-chart-carbs" : "text-primary"
                    }`}>
                      {predictionData.monthly_change_kg > 0 ? "+" : ""}{predictionData.monthly_change_kg}kg
                    </p>
                    <p className="text-xs text-muted-foreground">1 mes</p>
                  </div>
                  <div>
                    <p className={`font-mono text-lg font-bold ${
                      predictionData.three_month_change_kg > 0 ? "text-chart-carbs" : "text-primary"
                    }`}>
                      {predictionData.three_month_change_kg > 0 ? "+" : ""}{predictionData.three_month_change_kg}kg
                    </p>
                    <p className="text-xs text-muted-foreground">3 meses</p>
                  </div>
                </div>
                
                <div className="h-px bg-border/50" />
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Peso actual → 3 meses:</span>
                  <span className="font-mono font-semibold">
                    {predictionData.current_weight}kg → {predictionData.projected_weight_3_months}kg
                  </span>
                </div>
              </div>

              <div className={`mt-4 p-3 rounded-xl ${
                predictionData.direction === "déficit" ? "bg-primary/10" : 
                predictionData.direction === "superávit" ? "bg-chart-carbs/10" : "bg-muted"
              }`}>
                <p className="text-sm">{predictionData.message}</p>
              </div>
            </motion.div>
          )}

          {predictionData && !predictionData.has_prediction && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border/50 rounded-2xl p-6 flex items-center justify-center"
            >
              <div className="text-center">
                <Scale className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">{predictionData.message}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Días registrados: {predictionData.days_tracked}/3
                </p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Calorie Ring */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border/50 rounded-2xl p-6 md:row-span-2 flex flex-col items-center justify-center"
          >
            <div className="relative w-48 h-48">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="hsl(var(--muted))"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke={progressPercentage > 100 ? "hsl(var(--destructive))" : "hsl(var(--primary))"}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${progressPercentage * 2.83} 283`}
                  className="progress-ring"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Flame className="w-6 h-6 text-accent mb-1" />
                <span className="font-mono text-3xl font-bold" data-testid="dashboard-calories">
                  {Math.round(caloriesConsumed)}
                </span>
                <span className="text-sm text-muted-foreground">de {calorieGoal} kcal</span>
              </div>
            </div>
            <div className="mt-4 text-center">
              <p className={`font-semibold ${caloriesRemaining < 0 ? 'text-destructive' : 'text-primary'}`}>
                {caloriesRemaining >= 0 ? (
                  <span className="flex items-center justify-center gap-1">
                    <TrendingDown className="w-4 h-4" />
                    {Math.round(caloriesRemaining)} kcal restantes
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    {Math.abs(Math.round(caloriesRemaining))} kcal excedidas
                  </span>
                )}
              </p>
            </div>
          </motion.div>

          {/* Macro Cards */}
          {macroData.map((macro, index) => (
            <motion.div
              key={macro.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + index * 0.05 }}
              className="bg-card border border-border/50 rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${macro.color}20` }}
                  >
                    {macro.name === "Proteína" && <Beef className="w-5 h-5" style={{ color: macro.color }} />}
                    {macro.name === "Carbos" && <Wheat className="w-5 h-5" style={{ color: macro.color }} />}
                    {macro.name === "Grasa" && <Droplet className="w-5 h-5" style={{ color: macro.color }} />}
                  </div>
                  <span className="font-medium">{macro.name}</span>
                </div>
                <span className="text-sm text-muted-foreground">{macro.goal}g objetivo</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-baseline gap-1">
                  <span className="font-mono text-2xl font-bold">{Math.round(macro.value)}</span>
                  <span className="text-muted-foreground">g</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${Math.min((macro.value / macro.goal) * 100, 100)}%`,
                      backgroundColor: macro.color 
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {Math.round((macro.value / macro.goal) * 100)}% del objetivo
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Weekly Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card border border-border/50 rounded-2xl p-6"
        >
          <h3 className="font-heading font-semibold mb-4">Últimos 7 días</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis 
                  dataKey="date" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.75rem',
                    boxShadow: '0 4px 20px rgb(0,0,0,0.1)'
                  }}
                  formatter={(value) => [`${value} kcal`, 'Calorías']}
                />
                <Bar 
                  dataKey="calories" 
                  fill="hsl(var(--primary))" 
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Today's Meals */}
        {todayData?.meals && todayData.meals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-card border border-border/50 rounded-2xl p-6"
          >
            <h3 className="font-heading font-semibold mb-4">Comidas de hoy</h3>
            <div className="space-y-3">
              {todayData.meals.map((meal, index) => (
                <div 
                  key={meal.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-xl"
                >
                  <div>
                    <p className="font-medium">{meal.description}</p>
                    <p className="text-sm text-muted-foreground">{meal.time}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold">{Math.round(meal.total_calories)} kcal</p>
                    <p className="text-xs text-muted-foreground">
                      P: {Math.round(meal.total_protein)}g | C: {Math.round(meal.total_carbs)}g | G: {Math.round(meal.total_fat)}g
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </MainLayout>
  );
};

export default DashboardPage;
